'use client'

import React, { useState } from 'react'
import { Menu, X } from 'lucide-react'

import { useTenant } from '@rewardops/auth/src/client/use-tenant'
import { cn, LocalizedLink, useLocale } from '@rewardops/ui'
import { type CategorySummaryData } from '@rewardops/utils'

import { useCategoriesWithUrls } from '~/components/hooks/use-categories-with-urls'
import { useTenantStyles } from '~/components/hooks/use-tenant-styles'
import { Logo } from '~/components/logo'

interface MobileNavProps {
  categories?: CategorySummaryData[]
}

const MobileNav = ({ categories }: MobileNavProps) => {
  const [isOpen, setIsOpen] = useState(false)
  const { locale } = useLocale()
  const tenant = useTenant()
  const tenantStyles = useTenantStyles({ hover: true })
  const { showProducts, roRootCategory } = tenant
  const categoriesList = useCategoriesWithUrls(
    showProducts,
    categories,
    roRootCategory,
    locale,
  )

  if (!categoriesList) return null

  const toggleNav = () => setIsOpen(!isOpen)

  const Icon = isOpen ? X : Menu

  return (
    <nav
      className={cn(
        'z-50 h-12 w-full text-black transition-colors duration-500 ease-in-out',
        {
          'bg-white': isOpen,
        },
      )}
      {...tenantStyles}
    >
      <div className="container flex items-center justify-between py-1">
        <Logo />
        <button
          className="text-2xl"
          onClick={toggleNav}
          aria-label="Menu toggle button"
        >
          <Icon 
            className="text-black opacity-80 hover:text-black hover:opacity-100"
            role="img"
          >
            <title>Menu toggle</title>
          </Icon>
        </button>
      </div>
      {isOpen && (
        <div
          className={cn('flex h-screen flex-col bg-white pl-7')}
          onClick={toggleNav}
        >
          {categoriesList.map(({ category, categoryUrl }) => (
            <LocalizedLink
              key={category.code}
              className="py-2 text-3xl font-medium text-black opacity-80 hover:opacity-100"
              href={categoryUrl}
            >
              <span>{category.name[locale]}</span>
            </LocalizedLink>
          ))}
        </div>
      )}
    </nav>
  )
}

export { MobileNav }
